/*
 * DocTracePreview / DocTraceSlideOver — 15.A трассируемость «находка → лист ПД».
 *
 * Переиспользуемое ядро подсветки цитаты на реальной странице документа.
 * Механизм (общий для всех проверок):
 *   POST /documents/{id}/highlight {page, quote} → {found, page, rects[0..1]}
 *   GET  /documents/{id}/page/{n}.jpg            → JPEG листа (кэш на бэке)
 *
 * page = число  → конкретный лист (комплектность ПП-87 знает страницу).
 * page = null   → поиск цитаты ПО ВСЕМУ текст-слою (нормы/решения/задание —
 *                 у их находок нет страницы; бэк вернёт лист, где нашёл).
 *
 * Цитата должна быть ДОСЛОВНОЙ (фрагмент текст-слоя), иначе search_for мимо —
 * тогда честно показываем «не нашли» / лист целиком, а не пустой оверлей.
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSearch, Maximize2, X } from 'lucide-react';
import { api } from '@/api/client';

interface HighlightRect { x0: number; y0: number; x1: number; y1: number }
interface HighlightResp { found: boolean; page: number; rects: HighlightRect[] }

export interface DocTraceProps {
  documentId: string | null | undefined;
  page: number | null | undefined; // null/0 → поиск по всему документу
  quote: string | null | undefined;
}

const dashBox: React.CSSProperties = {
  fontSize: 13, padding: '18px 16px', background: '#F9FAFB',
  border: '1px dashed #E5E7EB', borderRadius: 10, textAlign: 'center',
};

export function DocTracePreview({ documentId, page, quote }: DocTraceProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const q = (quote || '').trim();
  const hasExplicitPage = typeof page === 'number' && page > 0;
  const hasDoc = !!documentId;

  const highlight = useQuery<HighlightResp>({
    // URL идёт БЕЗ /api-префикса — как и остальные asset-ручки клиента.
    queryKey: ['doc-trace-highlight', documentId, page ?? 0, q],
    queryFn: () => api.post<HighlightResp>(`/documents/${documentId}/highlight`, {
      page: hasExplicitPage ? page : 0,
      quote: q,
    }),
    enabled: hasDoc && !!q,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const resolvedPage = hasExplicitPage ? (page as number) : (highlight.data?.page || 0);
  const pageImgUrl = hasDoc && resolvedPage > 0
    ? `/documents/${documentId}/page/${resolvedPage}.jpg`
    : null;
  const rects = highlight.data?.found ? highlight.data.rects : [];

  if (!hasDoc) {
    return <div className="text-muted" style={dashBox}>Документ-источник к находке не привязан.</div>;
  }
  if (imgFailed) {
    return <div className="text-muted" style={dashBox}>Не удалось загрузить страницу документа.</div>;
  }
  // Doc-wide поиск ещё идёт (страницу узнаём только из ответа).
  if (!hasExplicitPage && highlight.isLoading) {
    return <p className="text-muted" style={{ fontSize: 12 }}>Ищем цитату в документе…</p>;
  }
  // Doc-wide поиск завершён, но цитаты нет в текст-слое → нечего показать.
  if (!hasExplicitPage && !pageImgUrl) {
    return (
      <div className="text-muted" style={dashBox}>
        {q ? 'Цитату не удалось найти в текст-слое документа (возможно, скан без текста).'
           : 'У находки нет цитаты для поиска в документе.'}
      </div>
    );
  }

  return (
    <div>
      {highlight.isLoading && hasExplicitPage && (
        <p className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>Ищем цитату на странице…</p>
      )}
      {/* Совпадение не нашлось (чертёж/графика без текст-слоя) → показываем сам лист
          БЕЗ подписи (решение владельца). «Найдено на стр.» — только для реального совпадения. */}
      {!hasExplicitPage && highlight.data?.found && (
        <p className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Найдено на стр. {resolvedPage}.
        </p>
      )}
      <div style={{ position: 'relative', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
        {pageImgUrl && (
          <img
            src={pageImgUrl}
            alt={`Страница ${resolvedPage} документа`}
            onError={() => setImgFailed(true)}
            style={{ display: 'block', width: '100%', height: 'auto' }}
          />
        )}
        {rects.map((r, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${r.x0 * 100}%`, top: `${r.y0 * 100}%`,
              width: `${(r.x1 - r.x0) * 100}%`, height: `${(r.y1 - r.y0) * 100}%`,
              background: 'rgba(245,158,11,.28)', border: '1.5px solid #F59E0B',
              borderRadius: 2, pointerEvents: 'none',
            }}
          />
        ))}
      </div>
      {pageImgUrl && (
        <a
          href={pageImgUrl} target="_blank" rel="noreferrer" className="text-muted"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, marginTop: 8, textDecoration: 'none' }}
        >
          <Maximize2 size={12} /> Открыть страницу целиком
        </a>
      )}
    </div>
  );
}

export interface DocTraceSlideOverProps {
  title: string;                 // шапка (напр. «СП 34.13330.2021»)
  subtitle?: string | null;      // мелким под шапкой (напр. документ/раздел)
  badge?: { label: string; color: string; bg: string } | null;
  bodyText?: string | null;      // текст находки/пункта
  quote?: string | null;         // цитата ДЛЯ ПОКАЗА (человекочитаемая)
  // Отдельный текст ДЛЯ ПОИСКА в текст-слое (когда показываемая цитата — пересказ
  // LLM/vision-описание, а искать надо по дословному спану). По умолчанию = quote.
  searchQuote?: string | null;
  documentId: string | null | undefined;
  page: number | null | undefined;
  onClose: () => void;
}

/** Универсальный slide-over «находка + место в документе» для проверок,
 *  где нет собственной панели трассируемости (нормы/решения/задание). */
export function DocTraceSlideOver(p: DocTraceSlideOverProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') p.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [p]);

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(10,22,40,.3)' }} onClick={p.onClose} />
      <aside
        className="fixed top-0 right-0 h-full bg-white border-l border-border flex flex-col z-50"
        style={{ width: 560, maxWidth: '100%', boxShadow: '-16px 0 32px rgba(10,22,40,.12)' }}
        role="dialog"
        aria-label="Находка и место в документе"
      >
        <header className="border-b border-border flex items-start justify-between" style={{ padding: '18px 22px', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h4 className="font-heading text-ink flex items-center" style={{ fontSize: 17, fontWeight: 700, gap: 8, margin: 0 }}>
              <FileSearch size={16} /> Находка в документе
            </h4>
            {p.subtitle && (
              <p className="text-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>{p.subtitle}</p>
            )}
          </div>
          <button type="button" onClick={p.onClose} className="text-muted" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} aria-label="Закрыть">
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto" style={{ padding: '16px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {p.badge && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                fontSize: 12, fontWeight: 600, color: p.badge.color, background: p.badge.bg,
                padding: '3px 10px', borderRadius: 20,
              }}>{p.badge.label}</span>
            )}
            <span className="text-ink" style={{ fontSize: 15, fontWeight: 700 }}>{p.title}</span>
          </div>

          {p.bodyText && (
            <div className="text-ink" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 4 }}>{p.bodyText}</div>
          )}
          {p.quote && (
            <div style={{
              marginTop: 12, fontSize: 13, color: '#374151', background: '#F9FAFB',
              borderLeft: '3px solid #059669', padding: '10px 14px', borderRadius: 6, fontStyle: 'italic',
            }}>
              «{p.quote}»
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', marginBottom: 10 }}>
              Место в документе
            </div>
            <DocTracePreview documentId={p.documentId} page={p.page} quote={p.searchQuote ?? p.quote} />
          </div>
        </div>
      </aside>
    </>
  );
}
